import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const CTASection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d7eoj25fi84lst2k0890', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const serviceOptions = [
    'Frameless Shower Doors', 'Sliding Glass Doors', 'Tub Enclosures',
    'Glass Railings', 'Custom Mirrors', 'Glass Partitions', 'Other',
  ];

  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/885b392454de8d894b36288a767a1e43.jpg"
          alt="Luxury glass installation Las Vegas"
          className="w-full h-full object-cover object-top"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/70"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">{t('freeEstimate')}</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t('cta_heading1')}<br />
              <span className="font-semibold">{t('cta_heading2')}</span>
            </h2>
            <p className="text-gray-300 text-base leading-relaxed mb-10 max-w-md">{t('cta_sub')}</p>
            <div className="flex flex-col gap-4">
              <a href="tel:+17026042093" className="inline-flex items-center gap-4 group cursor-pointer">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white text-black group-hover:bg-gray-100 transition-colors duration-300">
                  <i className="ri-phone-fill text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-bold text-xl">(702) 604-2093</p>
                  <p className="text-gray-400 text-xs">{t('cta_immediate')}</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/10 text-white">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-medium text-base">info@quinoluxglass.com</p>
                  <p className="text-gray-400 text-xs">{t('cta_reply')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white rounded-3xl p-8">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-black text-white mx-auto mb-4">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-black font-bold text-xl mb-2">{t('requestReceived')}</h3>
                <p className="text-gray-500 text-sm">{t('requestReceivedDesc')}</p>
              </div>
            ) : (
              <>
                <h3 className="text-black font-bold text-xl mb-1">{t('cta_form_heading')}</h3>
                <p className="text-gray-400 text-sm mb-6">{t('cta_form_sub')}</p>
                <form data-readdy-form id="home-quote-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">{t('fullName')} *</label>
                      <input type="text" name="name" required value={formData.name} onChange={handleChange} placeholder="John Smith" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black transition-colors" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">{t('phone_field')} *</label>
                      <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} placeholder="(702) 000-0000" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">{t('email')}</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">{t('serviceNeeded')} *</label>
                    <select name="service" required value={formData.service} onChange={handleChange} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-black focus:outline-none focus:border-black transition-colors cursor-pointer">
                      <option value="">{t('selectService')}</option>
                      {serviceOptions.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">{t('message')}</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} placeholder={t('tellUsProject')} rows={3} maxLength={500} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black transition-colors resize-none" />
                  </div>
                  <button type="submit" disabled={submitting} className="w-full bg-black text-white text-sm font-semibold py-4 rounded-xl hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60">
                    {submitting ? t('sendingLabel') : t('getMyFreeEstimate')}
                  </button>
                  <p className="text-gray-400 text-xs text-center">{t('submitConsent')}</p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
